// Facebook Login for Business launcher (Messenger Page connect).
//
// Opens Meta's OAuth dialog in a popup that redirects to a static callback
// page (public/facebook-callback.html). That page relays the returned `code`
// back here, and we hand the code + the exact redirect_uri to the
// facebook-messenger-onboard edge function, which does the token exchange
// server-side. Tokens never touch the browser.
//
// The app id and Login for Business configuration id come from the edge
// function's GET response, so turning the button on is a Supabase secret
// (META_MESSENGER_CONFIG_ID), not a frontend rebuild. The callback URL must be
// listed under Facebook Login for Business → Settings → Valid OAuth Redirect
// URIs in the Meta dashboard.

const GRAPH_VERSION = "v25.0";

// facebook.com's login pages can detach the popup from window.opener, so the
// callback page also relays through these same-origin channels.
export const FB_OAUTH_CHANNEL = "fb-oauth";
export const FB_OAUTH_STORAGE_KEY = "fb_oauth_result";
// After the popup closes, wait this long for a late BroadcastChannel/storage
// delivery before declaring the attempt cancelled.
const POPUP_CLOSE_GRACE_MS = 1500;

export interface FacebookLoginConfig {
  app_id: string;
  config_id: string;
}

export interface FacebookLoginResult {
  code: string;
  redirectUri: string;
}

export type FbOAuthOutcome =
  | { kind: "code"; code: string }
  | { kind: "error"; message: string }
  | null;

/** Interpret a relayed FB_OAUTH payload. Returns null when the payload isn't
 *  ours or its CSRF state doesn't match this launch attempt. Facebook always
 *  echoes state, so a missing one is treated as foreign. */
export function parseFbOAuthRelay(data: unknown, expectedState: string): FbOAuthOutcome {
  if (!data || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  if (d.type !== "FB_OAUTH" || d.state !== expectedState) return null;
  if (typeof d.error === "string" && d.error) {
    if (d.error === "access_denied") {
      return { kind: "error", message: "Facebook sign-in was cancelled." };
    }
    return {
      kind: "error",
      message:
        typeof d.error_description === "string" && d.error_description
          ? d.error_description
          : d.error,
    };
  }
  if (typeof d.code === "string" && d.code) {
    return { kind: "code", code: d.code };
  }
  return { kind: "error", message: "Facebook sign-in returned no code." };
}

export function getFacebookRedirectUri(): string {
  return `${window.location.origin}/facebook-callback.html`;
}

export function buildFacebookLoginUrl(args: {
  config: FacebookLoginConfig;
  redirectUri: string;
  state: string;
}): string {
  return (
    `https://www.facebook.com/${GRAPH_VERSION}/dialog/oauth?` +
    new URLSearchParams({
      client_id: args.config.app_id,
      config_id: args.config.config_id,
      redirect_uri: args.redirectUri,
      response_type: "code",
      override_default_response_type: "true",
      state: args.state,
    }).toString()
  );
}

/** Open the Facebook popup and resolve with the auth code + redirect_uri used
 *  (the backend needs the identical redirect_uri for the exchange). Rejects
 *  if the user closes the popup or Facebook returns an error. */
export async function launchFacebookPageLogin(
  config: FacebookLoginConfig,
): Promise<FacebookLoginResult> {
  const redirectUri = getFacebookRedirectUri();
  const state = crypto.randomUUID();
  const w = 600;
  const h = 760;
  const left = window.screenX + (window.outerWidth - w) / 2;
  const top = window.screenY + (window.outerHeight - h) / 2;
  const popup = window.open(
    buildFacebookLoginUrl({ config, redirectUri, state }),
    "facebook_page_login",
    `width=${w},height=${h},left=${left},top=${top}`,
  );
  if (!popup) {
    throw new Error("Popup blocked. Please allow popups and try again.");
  }

  return await new Promise<FacebookLoginResult>((resolve, reject) => {
    let settled = false;
    let graceTimer: number | undefined;
    let channel: BroadcastChannel | null = null;

    const cleanup = () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("storage", onStorage);
      clearInterval(closedTimer);
      if (graceTimer !== undefined) window.clearTimeout(graceTimer);
      try {
        channel?.close();
      } catch {
        /* ignore */
      }
      try {
        localStorage.removeItem(FB_OAUTH_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    };

    const settle = (outcome: FbOAuthOutcome) => {
      if (!outcome || settled) return;
      settled = true;
      cleanup();
      try {
        popup.close();
      } catch {
        /* ignore */
      }
      if (outcome.kind === "code") {
        resolve({ code: outcome.code, redirectUri });
      } else {
        reject(new Error(outcome.message));
      }
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      settle(parseFbOAuthRelay(event.data, state));
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key !== FB_OAUTH_STORAGE_KEY || !event.newValue) return;
      try {
        settle(parseFbOAuthRelay(JSON.parse(event.newValue), state));
      } catch {
        /* ignore malformed writes */
      }
    };

    // Direct read for races where the write landed before our listener saw
    // it. The state check makes leftovers from other attempts inert.
    const readStoredResult = (): FbOAuthOutcome => {
      try {
        const raw = localStorage.getItem(FB_OAUTH_STORAGE_KEY);
        return raw ? parseFbOAuthRelay(JSON.parse(raw), state) : null;
      } catch {
        return null;
      }
    };

    window.addEventListener("message", onMessage);
    window.addEventListener("storage", onStorage);
    try {
      channel = new BroadcastChannel(FB_OAUTH_CHANNEL);
      channel.onmessage = (event) => settle(parseFbOAuthRelay(event.data, state));
    } catch {
      channel = null; // BroadcastChannel unavailable — storage path still works
    }

    // The callback page closes itself right after relaying, so give its
    // delivery a short grace window before treating the close as a cancel.
    const closedTimer = setInterval(() => {
      if (!popup.closed || settled || graceTimer !== undefined) return;
      settle(readStoredResult());
      if (settled) return;
      graceTimer = window.setTimeout(() => {
        settle(readStoredResult());
        if (!settled) {
          settled = true;
          cleanup();
          reject(new Error("Facebook sign-in was cancelled."));
        }
      }, POPUP_CLOSE_GRACE_MS);
    }, 500);
  });
}
